import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../contexts/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Send, Bot, User, ChevronDown, Cpu, Plus, MessageSquare, Trash2,
  LayoutDashboard, Upload, BarChart3, Activity, Sparkles,
  FileText, Calendar, Clock, ArrowLeft, Wrench, CheckCircle2,
  Mic, MicOff,
} from "lucide-react";

interface Msg { id?: number; role: string; content: string; }
interface Conversation { id: number; title: string; model: string; message_count: number; last_message: string; updated_at: string; }
interface VersionInfo {
  version: string;
  upload_count: number;
  total_records: number;
  last_report_date: string;
  last_upload: string;
}

const DATA_SUGGESTIONS = [
  "How many consultations were done this year?",
  "What's the occupancy rate for Cardiology?",
  "Show me the billing trend by month",
  "Which health unit has the most consultations?",
  "What is the waiting list for Cardiology?",
  "Compare consultations vs last year by month",
];

const PRESENTATION_SUGGESTIONS = [
  "Create a presentation: Cardiology July 2025",
  "Create a presentation: monthly management report",
  "Create a presentation: cardiology variance analysis",
];

const QUICK_ACTIONS = [
  { label: "Upload Report", icon: Upload, to: "/upload" },
  { label: "Dashboard", icon: LayoutDashboard, to: "/dashboard" },
  { label: "Data Analysis", icon: BarChart3, to: "/analysis" },
  { label: "Token Usage", icon: Activity, to: "/tokens" },
];

function greeting(name: string) {
  const hour = new Date().getHours();
  if (hour % 2 === 0) return `Welcome back, ${name}`;
  if (hour >= 5 && hour < 12) return `Good morning, ${name}`;
  if (hour >= 12 && hour < 18) return `Good afternoon, ${name}`;
  return `Good evening, ${name}`;
}

interface Agent {
  id: number;
  name: string;
  description?: string;
  model?: string;
}

export default function ChatPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConv, setActiveConv] = useState<number | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [models, setModels] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState("deepseek-v4-flash");
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [showChatDropdown, setShowChatDropdown] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const recognitionRef = useRef<any>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const uploadSelectedFile = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadMessage(null);
    try {
      await api.upload(selectedFile, "Cardiology", "daily_activity");
      setUploadMessage("Upload completed.");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetch("/api/version")
        .then((r) => r.json())
        .then(setVersion)
        .catch(() => {});
    } catch (e: any) {
      setUploadMessage(e.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const sr = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    setVoiceSupported(!!sr);
  }, []);

  useEffect(() => {
    api.getModels().then((data) => setModels(data.models || [])).catch(() => {});
    api.listAgents()
      .then((data) => {
        const list = data.agents || [];
        setAgents(list);
        const def = list.find((a: Agent) => (a as any).is_default) || list[0];
        if (def) {
          setSelectedAgent(def);
          if (def.model) setSelectedModel(def.model);
        }
      })
      .catch(() => {});
    loadConversations();
    fetch("/api/version")
      .then((r) => r.json())
      .then(setVersion)
      .catch(() => {});
  }, []);

  const loadConversations = () => api.getConversations().then(setConversations).catch(() => {});
  const scrollToBottom = useCallback(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, []);
  useEffect(() => { scrollToBottom(); }, [messages, streamingText]);

  const startNewChat = () => { setActiveConv(null); setMessages([]); setStreamingText(""); setShowChatDropdown(false); setShowSuggestions(false); };
  const selectConversation = async (id: number) => {
    setActiveConv(id); setStreamingText(""); setShowChatDropdown(false); setShowSuggestions(false);
    try {
      const data = await api.getConversationMessages(id);
      setMessages(data.messages || []);
      if (data.model) setSelectedModel(data.model);
    } catch { setMessages([]); }
  };
  const deleteConv = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation(); await api.deleteConversation(id);
    if (activeConv === id) startNewChat(); loadConversations();
  };

  const send = async (textOverride?: string) => {
    const messageText = textOverride?.trim() || input.trim();
    if (!messageText || loading) return;

    // Detect presentation command
    const presentMatch = messageText.match(/^presenta[cç][aã]o[:\s]*(.+)|^presentation[:\s]*(.+)|^create a presentation[:\s]*(.+)/i);
    const presentTopic = presentMatch?.[1] || presentMatch?.[2] || presentMatch?.[3];
    if (presentTopic) {
      setMessages((m) => [...m, { role: "user", content: messageText }]);
      setInput(""); setLoading(true); setStreamingText(""); setShowSuggestions(false); setListening(false);
      try {
        const data = await api.generatePresentation(presentTopic, selectedModel, selectedAgent?.id);
        const downloadUrl = data.url;
        const assistantMessage = `Here is your presentation: **[${data.filename}](${downloadUrl})**.\n\nOutline preview:\n\n${data.outline_preview}`;
        setMessages((m) => [...m, { role: "assistant", content: assistantMessage }]);
      } catch (e: any) {
        setMessages((m) => [...m, { role: "assistant", content: `Error generating presentation: ${e.message}` }]);
      } finally {
        setLoading(false);
      }
      return;
    }

    setMessages((m) => [...m, { role: "user", content: messageText }]);
    setInput(""); setLoading(true); setStreamingText(""); setShowSuggestions(false); setListening(false);
    abortRef.current = new AbortController();
    const token = localStorage.getItem("token");
    try {
      const resp = await fetch("/api/agent/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message: messageText, model: selectedModel, conversation_id: activeConv, agent_id: selectedAgent?.id }),
        signal: abortRef.current.signal,
      });
      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = "", fullText = "";
      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n"); buffer = lines.pop() || "";
          let eventType = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) eventType = line.slice(7);
            else if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                if (eventType === "conversation" && data.conversation_id) setActiveConv(data.conversation_id);
                else if (eventType === "delta") { fullText += data.text || ""; setStreamingText(fullText); }
                else if (eventType === "done") { fullText = data.reply || fullText; setStreamingText(""); setMessages((m) => [...m, { role: "assistant", content: fullText }]); }
                else if (eventType === "error") { setStreamingText(""); setMessages((m) => [...m, { role: "assistant", content: `Error: ${data.error || "Error"}` }]); }
              } catch {}
            }
          }
        }
      }
      loadConversations();
    } catch (e: any) {
      if (e.name !== "AbortError") { setStreamingText(""); setMessages((m) => [...m, { role: "assistant", content: `Error: ${e.message}` }]); }
    } finally { setLoading(false); setStreamingText(""); }
  };

  const stop = () => {
    abortRef.current?.abort();
    if (streamingText) setMessages((m) => [...m, { role: "assistant", content: streamingText }]);
    setStreamingText(""); setLoading(false);
  };

  const startListening = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.lang = "pt-PT";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (event: any) => {
      let finalTranscript = "";
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += transcript;
        else interimTranscript += transcript;
      }
      const combined = (finalTranscript || interimTranscript).trim();
      if (combined) setInput(combined);
      if (finalTranscript && combined) {
        recognition.stop();
        recognitionRef.current = null;
        setListening(false);
        send(combined);
      }
    };
    recognition.onerror = (event: any) => {
      if (event.error !== "aborted") {
        setListening(false);
      }
    };
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setListening(false);
  };

  const toggleListening = () => {
    if (listening) stopListening();
    else startListening();
  };

  const currentModelName = models.find((m) => m.id === selectedModel)?.name || selectedModel;
  const activeConvTitle = conversations.find((c) => c.id === activeConv)?.title || "New Chat";
  const hasMessages = messages.length > 0 || streamingText.length > 0;

  const firstName = user?.name?.split(" ")[0] || user?.email?.split("@")[0] || "there";
  const insight = version
    ? `You have ${version.total_records.toLocaleString()} records from ${version.upload_count} uploads for the ${version.last_report_date} report.`
    : "Ask me anything about your Cardiology data.";

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Top bar */}
      <header className="h-14 border-b border-slate-100 flex items-center justify-between px-4 bg-white shrink-0">
        <div className="flex items-center gap-2">
          {hasMessages && (
            <button
              onClick={startNewChat}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 rounded-lg text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-slate-500" />
            </button>
          )}

          <div className="relative">
            <button
              onClick={() => setShowChatDropdown(!showChatDropdown)}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 rounded-lg text-sm transition-colors"
            >
              <MessageSquare className="w-4 h-4 text-slate-400" />
              <span className="font-medium text-slate-700 max-w-[160px] truncate">{activeConvTitle}</span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>
            {showChatDropdown && (
              <div className="absolute left-0 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-2 max-h-96 overflow-y-auto">
                <button
                  onClick={startNewChat}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-novacare-700 hover:bg-novacare-50 font-medium border-b border-slate-100"
                >
                  <Plus className="w-4 h-4" /> New Chat
                </button>
                {conversations.length > 0 && <div className="pt-1">
                  {conversations.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => selectConversation(c.id)}
                      className={`group flex items-center gap-2 px-4 py-2 cursor-pointer text-sm ${
                        activeConv === c.id ? "bg-novacare-50 text-novacare-800 font-medium" : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <MessageSquare className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="flex-1 truncate">{c.title}</span>
                      <button onClick={(e) => deleteConv(c.id, e)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <div className="relative">
            <button
              onClick={() => setShowAgentDropdown(!showAgentDropdown)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <Bot className="w-4 h-4 text-novacare-700" />
              <span className="font-medium hidden sm:inline truncate max-w-[120px]">{selectedAgent?.name || "Default Agent"}</span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>
            {showAgentDropdown && (
              <div className="absolute right-0 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-2 max-h-80 overflow-y-auto">
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 border-b border-slate-100">Select agent</div>
                {agents.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => { setSelectedAgent(a); if (a.model) setSelectedModel(a.model); setShowAgentDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-novacare-50 ${a.id === selectedAgent?.id ? "bg-novacare-50 text-novacare-800 font-medium" : "text-slate-700"}`}
                  >
                    <div className="font-medium">{a.name}</div>
                    {a.description && <div className="text-xs text-slate-400 truncate">{a.description}</div>}
                  </button>
                ))}
                <a
                  href="/agents"
                  className="block w-full text-left px-3 py-2 text-sm text-novacare-700 hover:bg-novacare-50 border-t border-slate-100"
                >
                  + Manage agents
                </a>
              </div>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowModelDropdown(!showModelDropdown)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <Cpu className="w-4 h-4 text-novacare-700" />
              <span className="font-medium hidden sm:inline">{currentModelName}</span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>
            {showModelDropdown && (
              <div className="absolute right-0 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-2 max-h-80 overflow-y-auto">
                <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 border-b border-slate-100">Select model</div>
                {models.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => { setSelectedModel(m.id); setShowModelDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-novacare-50 ${m.id === selectedModel ? "bg-novacare-50 text-novacare-800 font-medium" : "text-slate-700"}`}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={startNewChat}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              title="New chat"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main scroll area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-10">
          {!hasMessages ? (
            <div className="flex flex-col items-center">
              {/* Greeting */}
              <div className="text-center mb-3">
                <h1 className="text-4xl sm:text-5xl font-light text-slate-900 tracking-tight">
                  {greeting(firstName)}
                </h1>
                <p className="mt-3 text-slate-500 text-lg">{insight}</p>
              </div>

              {/* Big input */}
              <div className="w-full max-w-2xl mt-8">
                <div className="bg-slate-50 border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="How can I help you today?"
                    disabled={loading}
                    rows={1}
                    className="w-full bg-transparent border-0 px-5 py-4 text-base text-slate-800 placeholder:text-slate-400 focus:ring-0 focus:outline-none disabled:opacity-50 resize-none"
                    style={{ minHeight: "56px", maxHeight: "160px" }}
                  />
                  <div className="flex items-center justify-between px-3 pb-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".xlsx,.xls,.pdf,.csv"
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading || uploading}
                        title="Upload file"
                        className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 rounded-lg transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                      {selectedFile && <span className="text-xs text-slate-500 truncate max-w-[120px]">{selectedFile.name}</span>}
                      {selectedFile && (
                        <button
                          onClick={uploadSelectedFile}
                          disabled={uploading}
                          title="Send file"
                          className="text-xs bg-novacare-700 hover:bg-novacare-800 text-white px-2 py-1 rounded disabled:opacity-50"
                        >
                          {uploading ? "Uploading" : "Send"}
                        </button>
                      )}
                      {uploadMessage && <span className="text-xs text-slate-500">{uploadMessage}</span>}
                      {voiceSupported && (
                        <button
                          onClick={toggleListening}
                          disabled={loading || uploading}
                          title={listening ? "Stop listening" : "Talk to assistant"}
                          className={`p-2 rounded-lg transition-colors ${
                            listening
                              ? "bg-red-50 text-red-600 animate-pulse"
                              : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
                          }`}
                        >
                          {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                        </button>
                      )}
                      {listening && <span className="text-xs text-red-500 font-medium">Listening…</span>}
                    </div>
                    {loading ? (
                      <button onClick={stop} className="p-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors">
                        <span className="w-2 h-2 bg-red-500 rounded-full" />
                      </button>
                    ) : (
                      <button
                        onClick={() => send()}
                        disabled={!input.trim()}
                        className="p-2 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white rounded-lg transition-colors"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex flex-wrap justify-center gap-2 mt-6">
                {QUICK_ACTIONS.map((action) => (
                  <button
                    key={action.label}
                    onClick={() => navigate(action.to)}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm text-slate-700 hover:border-novacare-400 hover:bg-novacare-50 transition-colors"
                  >
                    <action.icon className="w-4 h-4 text-slate-500" />
                    {action.label}
                  </button>
                ))}
              </div>

              {/* Suggested questions */}
              <div className="w-full max-w-2xl mt-10">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 text-center">Suggested questions</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {DATA_SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => { setInput(s); setShowSuggestions(false); }}
                      className="text-left px-4 py-3 bg-white border border-slate-200 rounded-xl hover:border-novacare-400 hover:bg-novacare-50 text-sm text-slate-700 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-full max-w-2xl mt-8">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 text-center">Create a presentation</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {PRESENTATION_SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => { setInput(s); setShowSuggestions(false); }}
                      className="text-left px-4 py-3 bg-white border border-slate-200 rounded-xl hover:border-novacare-400 hover:bg-novacare-50 text-sm text-slate-700 transition-colors flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4 text-novacare-700" />
                      {s.replace("Create a presentation: ", "")}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stats cards */}
              {version && <div className="w-full mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={FileText} label="Total Records" value={version.total_records.toLocaleString()} sub="across all uploads" />
                <StatCard icon={Upload} label="Uploads" value={String(version.upload_count)} sub="reports loaded" />
                <StatCard icon={Calendar} label="Report Date" value={version.last_report_date} sub="active period" />
                <StatCard icon={Clock} label="Last Updated" value={version.last_upload ? new Date(version.last_upload).toLocaleDateString() : "-"} sub="data refreshed" />
              </div>}
            </div>
          ) : (
            <div className="space-y-6 pb-4">
              {messages.map((msg, i) => <MessageBubble key={i} role={msg.role} content={msg.content} />)}
              {streamingText && (
                <div className="flex gap-4">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-novacare-100 flex items-center justify-center"><Bot className="w-4 h-4 text-novacare-800" /></div>
                  <div className="flex-1">
                    <div className="prose prose-sm prose-slate max-w-none text-slate-800"><ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText}</ReactMarkdown></div>
                    <span className="inline-block w-1.5 h-4 bg-novacare-600 animate-pulse rounded-sm ml-0.5 mt-1" />
                  </div>
                </div>
              )}
              {loading && !streamingText && <ToolBadge icon={Wrench} label="Running action" />}
            </div>
          )}
        </div>
      </div>

      {/* Reply input - only when conversation is active */}
      {hasMessages && (
        <div className="border-t border-slate-100 bg-white px-4 py-3 shrink-0">
          <div className="max-w-3xl mx-auto">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl hover:shadow-md transition-shadow">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Reply..."
                disabled={loading}
                rows={1}
                className="w-full bg-transparent border-0 px-5 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:ring-0 focus:outline-none disabled:opacity-50 resize-none"
                style={{ minHeight: "48px", maxHeight: "120px" }}
              />
              <div className="flex items-center justify-between px-3 pb-3">
                <div className="flex items-center gap-1">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx,.xls,.pdf,.csv"
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading || uploading}
                    title="Upload file"
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 rounded-lg transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                  </button>
                  {selectedFile && <span className="text-xs text-slate-500 truncate max-w-[100px]">{selectedFile.name}</span>}
                  {selectedFile && (
                    <button
                      onClick={uploadSelectedFile}
                      disabled={uploading}
                      title="Send file"
                      className="text-xs bg-novacare-700 hover:bg-novacare-800 text-white px-2 py-1 rounded disabled:opacity-50"
                    >
                      {uploading ? "Uploading" : "Send"}
                    </button>
                  )}
                  {uploadMessage && <span className="text-xs text-slate-500">{uploadMessage}</span>}
                  {voiceSupported && (
                    <button
                      onClick={toggleListening}
                      disabled={loading || uploading}
                      title={listening ? "Stop listening" : "Talk to assistant"}
                      className={`p-2 rounded-lg transition-colors ${
                        listening
                          ? "bg-red-50 text-red-600 animate-pulse"
                          : "text-slate-400 hover:text-slate-600 hover:bg-slate-200/50"
                      }`}
                    >
                      {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </button>
                  )}
                  {listening && <span className="text-xs text-red-500 font-medium ml-1">Listening…</span>}
                </div>
                {loading ? (
                  <button onClick={stop} className="p-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors">
                    <span className="w-2 h-2 bg-red-500 rounded-full" />
                  </button>
                ) : (
                  <button
                    onClick={() => send()}
                    disabled={!input.trim()}
                    className="p-2 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white rounded-lg transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {showSuggestions && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {DATA_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => { setInput(s); setShowSuggestions(false); }}
                    className="text-left px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-novacare-400 hover:bg-novacare-50 text-xs text-slate-700 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PRESENTATION_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => { setInput(s); setShowSuggestions(false); }}
                  className="text-left px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-novacare-400 hover:bg-novacare-50 text-xs text-slate-700 transition-colors flex items-center gap-2"
                >
                  <FileText className="w-3 h-3 text-novacare-700" />
                  {s.replace("Create a presentation: ", "")}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400 text-center mt-1.5">AI may produce inaccurate information · {currentModelName}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolBadge({ icon: Icon, label }: { icon: any; label: string }) {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500">
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-md border border-slate-200">
        <Icon className="w-3 h-3" /> {label}
      </span>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }: { icon: any; label: string; value: string; sub: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-novacare-400 transition-colors">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-400" />
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl font-semibold text-slate-900">{value}</p>
      <p className="text-xs text-slate-400 mt-1">{sub}</p>
    </div>
  );
}

function MessageBubble({ role, content }: { role: string; content: string }) {
  const isUser = role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="bg-slate-100 text-slate-900 rounded-2xl rounded-tr-md px-4 py-3 max-w-[85%]">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{content}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-4">
      <div className="shrink-0 w-8 h-8 rounded-full bg-novacare-100 flex items-center justify-center">
        <Bot className="w-4 h-4 text-novacare-800" />
      </div>
      <div className="flex-1">
        <div className="prose prose-sm prose-slate max-w-none text-slate-800 [&_table]:border-collapse [&_th]:border [&_td]:border [&_th]:border-slate-300 [&_td]:border-slate-300 [&_th]:px-2 [&_td]:px-2 [&_th]:py-1 [&_td]:py-1 [&_table]:text-xs">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" download={props.href?.endsWith('.pptx') ? true : undefined} />
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
        <ToolBadge icon={CheckCircle2} label="Used data query tools" />
      </div>
    </div>
  );
}